import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  KeyRound,
  Loader2,
  Save,
  Trash2,
  UserCog,
  UserPlus,
} from "lucide-react";
import { Navigate } from "react-router-dom";
import type { AccountDTO, ManajerDTO } from "@app/shared";
import { api } from "../lib/api";
import { useBrands } from "../lib/brand";
import { cn } from "../lib/cn";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { useToast } from "../components/Toast";

function msg(e: unknown): string {
  return e instanceof Error ? e.message : "Terjadi kesalahan";
}

/**
 * Admin "Manajer / Tim" page (`/manajers`). Create manajer logins and assign
 * which brands each may manage. A manajer with NO brand selected manages ALL
 * brands (the backward-compatible default); selecting brands restricts them to
 * exactly those. Admin-only.
 */
export function Manajers() {
  const { isAdmin } = useBrands();
  if (!isAdmin) return <Navigate to="/drafts" replace />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-navy">Manajer / Tim</h1>
        <p className="mt-1 text-sm text-slate-500">
          Buat akun manajer dan tentukan brand mana saja yang boleh mereka kelola.
          Manajer <b>tanpa</b> brand terpilih dapat mengelola <b>semua</b> brand.
        </p>
      </div>
      <CreateManajerCard />
      <ManajerList />
    </div>
  );
}

/** Create-a-manajer form (email + password). New manajer defaults to all brands. */
function CreateManajerCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const create = useMutation({
    mutationFn: () => api.createManajer({ email: email.trim(), password }),
    onSuccess: (m) => {
      toast.success(`Manajer dibuat: ${m.email}`);
      setEmail("");
      setPassword("");
      void qc.invalidateQueries({ queryKey: ["manajers"] });
    },
    onError: (e) => toast.error(msg(e)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Buat manajer baru</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <Input
            type="email"
            placeholder="email@tim.com"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            type="password"
            placeholder="Password (min 8)"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            disabled={
              create.isPending || !email.includes("@") || password.length < 8
            }
          >
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="h-4 w-4" />
            )}
            Buat manajer
          </Button>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Setelah dibuat, pilih brand yang boleh dikelola di daftar bawah. Default:
          semua brand.
        </p>
      </CardContent>
    </Card>
  );
}

/** Lists all manajers, each with its brand-assignment editor + admin actions. */
function ManajerList() {
  const managersQuery = useQuery({
    queryKey: ["manajers"],
    queryFn: ({ signal }) => api.manajers(signal),
  });
  const brandsQuery = useQuery({
    queryKey: ["accounts"],
    queryFn: ({ signal }) => api.accounts(signal),
  });
  const managers = managersQuery.data ?? [];
  const brands = brandsQuery.data ?? [];

  if (managersQuery.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-teal" />
      </div>
    );
  }
  if (managers.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-slate-500">
          Belum ada manajer. Buat satu di atas.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      {managers.map((m) => (
        <ManajerRow key={m.id} manajer={m} brands={brands} />
      ))}
    </div>
  );
}

/** One manajer: brand-assignment toggles + reset-password + delete. */
function ManajerRow({
  manajer,
  brands,
}: {
  manajer: ManajerDTO;
  brands: AccountDTO[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<string[]>(manajer.brandKeys);
  const [resetOpen, setResetOpen] = useState(false);
  const [pw, setPw] = useState("");

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ["manajers"] });

  const saveBrands = useMutation({
    mutationFn: () =>
      api.setManajerBrands(manajer.id, { brandKeys: selected }),
    onSuccess: () => {
      toast.success(
        "Brand manajer diperbarui — berlaku saat manajer login berikutnya.",
      );
      invalidate();
    },
    onError: (e) => toast.error(msg(e)),
  });

  const reset = useMutation({
    mutationFn: () =>
      api.resetManajerPassword(manajer.id, { password: pw }),
    onSuccess: () => {
      toast.success(
        `Password ${manajer.email} direset — manajer diminta membuat password baru saat login.`,
      );
      setResetOpen(false);
      setPw("");
      invalidate();
    },
    onError: (e) => toast.error(msg(e)),
  });

  const del = useMutation({
    mutationFn: () => api.deleteManajer(manajer.id),
    onSuccess: () => {
      toast.success("Manajer dihapus");
      invalidate();
    },
    onError: (e) => toast.error(msg(e)),
  });

  const toggle = (key: string) =>
    setSelected((cur) =>
      cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key],
    );
  const norm = (a: string[]) => a.slice().sort().join(",");
  const dirty = norm(selected) !== norm(manajer.brandKeys);
  const allBrands = selected.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <UserCog className="h-4 w-4 text-teal" />
            {manajer.email}
            {manajer.mustChangePassword && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                Menunggu ganti password
              </span>
            )}
          </span>
          <Badge variant={allBrands ? "teal" : "secondary"}>
            {allBrands ? "Semua brand" : `${selected.length} brand`}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="mb-1 block">Brand yang boleh dikelola</Label>
          <p className="mb-2 text-xs text-slate-500">
            Tidak ada yang dipilih = manajer boleh mengelola <b>semua</b> brand.
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {brands.map((b) => {
              const on = selected.includes(b.key);
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => toggle(b.key)}
                  className={cn(
                    "flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors",
                    on
                      ? "border-teal bg-teal/10 text-navy"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                  )}
                >
                  <span
                    className="inline-block h-3 w-3 shrink-0 rounded-full ring-1 ring-slate-300"
                    style={{ backgroundColor: b.accentColor ?? "#33BEBA" }}
                  />
                  <span className="min-w-0 truncate">{b.displayName}</span>
                  {on && (
                    <Check className="ml-auto h-4 w-4 shrink-0 text-teal" />
                  )}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              onClick={() => saveBrands.mutate()}
              disabled={saveBrands.isPending || !dirty}
            >
              {saveBrands.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Simpan brand
            </Button>
            {selected.length > 0 && (
              <Button variant="ghost" onClick={() => setSelected([])}>
                Kosongkan (semua brand)
              </Button>
            )}
            {dirty && (
              <span className="text-xs text-amber-600">Belum disimpan</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
          <Button variant="outline" onClick={() => setResetOpen((v) => !v)}>
            <KeyRound className="h-4 w-4" />
            Reset password
          </Button>
          <Button
            variant="ghost"
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={() => {
              if (window.confirm(`Hapus manajer ${manajer.email}?`))
                del.mutate();
            }}
            disabled={del.isPending}
          >
            <Trash2 className="h-4 w-4" />
            Hapus
          </Button>
        </div>
        {resetOpen && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
            <Input
              type="password"
              placeholder="Password baru (min 8)"
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
            />
            <Button
              variant="primary"
              onClick={() => reset.mutate()}
              disabled={reset.isPending || pw.length < 8}
            >
              {reset.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Simpan password"
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
